'use client';
import { useMemo, useState, useCallback } from 'react';
import {
  Button,
  CommentItem,
  IMessage,
  IMessagesQueryParams,
  INormalizedMessagesOfThreadResponse,
  useMessageContext,
} from '@ui/components';
import { ContextUserType } from '@ui/constants/context';
import { CommentItemSkeleton } from '../comments/comment-item-skeleton';

interface Props {
  userType: ContextUserType;
  comment: IMessage;
  creator_id: string;
  user_id: string;
  parent_comment_thread_id?: string;
  filter_by_reply_id?: string;
  moderators_ids: string[];
}

export const ReplyList = ({
  userType,
  comment,
  creator_id,
  user_id,
  parent_comment_thread_id,
  filter_by_reply_id,
  moderators_ids,
}: Props) => {
  const messageState = useMessageContext(userType);
  const [query, setQuery] = useState<IMessagesQueryParams>({
    ordering: 'asc',
    page: 1,
    pageSize: 5,
    thread_id: comment.child_thread_id,
    conversation_id: comment.conversation_id,
    creator_id,
    fan_id: user_id,
    ...(filter_by_reply_id && {
      message_ids: filter_by_reply_id,
    }),
  });

  const { data, isLoading, isSuccess } = messageState.useGetMessagesOfThreadQuery(query, {
    skip: !query.thread_id || !query.creator_id || !query.conversation_id,
    selectFromResult: ({ data, ...params }: { data: INormalizedMessagesOfThreadResponse }) => ({
      data: {
        ...data,
        messages: messageState.messagesSelector.selectAll(
          data ? data.messages : messageState.messagesAdapter.getInitialState(),
        ),
      },
      ...params,
    }),
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const totalMessagesBeforeFilter = useMemo(
    () => data?.totalMessagesBeforeFilter,
    [data?.totalMessagesBeforeFilter],
  );

  const replies = useMemo(() => data.messages, [data.messages]);
  const communityDetails = useMemo(() => data?.community_details, [data?.community_details]);

  const hasMorePages = useMemo(
    () => (isSuccess && data ? query.page && query.page < data.totalPages : false),
    [data, query.page, isSuccess],
  );

  const onNextPage = useCallback(
    () => setQuery((prev) => ({ ...prev, page: prev.page && prev.page + 1 })),
    [],
  );

  const handleShowAllReplies = useCallback(() => {
    setQuery((prev) => {
      const newQuery = {
        ...prev,
      };

      delete newQuery.message_ids;
      return newQuery;
    });
  }, [query]);

  if (isLoading && replies.length === 0) {
    return (
      <div className="flex flex-col gap-y-6 pt-4">
        <CommentItemSkeleton />
        <CommentItemSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div>
        {replies.map((reply: IMessage) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            userType={userType}
            user_id={messageState.user_id}
            creator_id={creator_id}
            enableReply
            query={query}
            reportLabel="Report reply"
            isReply
            communityDetails={messageState.community_details || communityDetails}
            isAuthenticated={messageState.isAuthenticated}
            isFanOfCreator={messageState.isFanOfCreator}
            parent_comment_thread_id={parent_comment_thread_id}
            hierarchyType="reply"
            moderators_ids={moderators_ids}
          />
        ))}
        {isLoading && replies.length > 0 && (
          <div className="py-4">
            <CommentItemSkeleton />
          </div>
        )}
      </div>

      {totalMessagesBeforeFilter > 0 && query.message_ids && (
        <div className="flex justify-center py-4">
          <Button type="secondary" size="small" onClick={handleShowAllReplies}>
            Show all replies
          </Button>
        </div>
      )}

      {hasMorePages && !isLoading && (
        <div className="flex justify-center py-4">
          <Button type="secondary" size="small" onClick={onNextPage} disabled={isLoading}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};
